<template>
    <div class="l-chart">
        <canvas ref="canvas" :height="height"></canvas>
    </div>
</template>

<script>
import Chart from "chart.js";

export default {
    props: {
        type: {
            type: String,
            required: true,
        },
        data: {
            type: Object,
            required: true,
        },
        colors: {
            type: Array,
            default: () => [],
        },
        options: {
            type: Object,
            required: true,
        },
        height: {
            type: String,
            default: "400",
        },
    },
    data() {
        return {
            chart: null,
            ctx: null,
        };
    },
    computed: {
        colorSet() {
            return {
                primary: {
                    high: "rgba(93, 93, 233, 0.8)",
                    mid: "rgba(93, 93, 233, 0.2)",
                    low: "rgba(93, 93, 233, 0)",
                },
                red: {
                    high: "rgba(192, 38, 52, 0.8)",
                    mid: "rgba(192, 38, 52, 0.3)",
                    low: "rgba(192, 38, 52, 0)",
                },
                green: {
                    high: "rgba(66, 125, 109, 0.8)",
                    mid: "rgba(66, 125, 109, 0.2)",
                    low: "rgba(66, 125, 109, 0)",
                },
                orange: {
                    high: "rgba(209, 108, 49, 0.8)",
                    mid: "rgba(209, 108, 49, 0.3)",
                    low: "rgba(209, 108, 49, 0)",
                },
                grey: {
                    high: "rgba(188, 196, 218, 0.8)",
                    mid: "rgba(188, 196, 218, 0.3)",
                    low: "rgba(188, 196, 218, 0)",
                },
                primaryLow: {
                    high: "rgba(93, 93, 233, 0.5)",
                    mid: "rgba(93, 93, 233, 0.1)",
                    low: "rgba(93, 93, 233, 0)",
                },
                redLow: {
                    high: "rgba(192, 38, 52, 0.5)",
                    mid: "rgba(192, 38, 52, 0.1)",
                    low: "rgba(192, 38, 52, 0)",
                },
                greenLow: {
                    high: "rgba(66, 125, 109, 0.5)",
                    mid: "rgba(66, 125, 109, 0.1)",
                    low: "rgba(66, 125, 109, 0)",
                },
                orangeLow: {
                    high: "rgba(209, 108, 49, 0.5)",
                    mid: "rgba(209, 108, 49, 0.1)",
                    low: "rgba(209, 108, 49, 0)",
                },
                greyLow: {
                    high: "rgba(188, 196, 218, 0.5)",
                    mid: "rgba(188, 196, 218, 0.1)",
                    low: "rgba(188, 196, 218, 0)",
                },
                default: {
                    high: "rgba(93, 93, 233, 1)",
                    mid: "rgba(93, 93, 233, 0.2)",
                    low: "rgba(93, 93, 233, 0)",
                },
            };
        },
        gradientColors() {
            let gradientColors = {};

            for (const [key, value] of Object.entries(this.colorSet)) {
                let gradient = this.ctx.createLinearGradient(0, 0, 0, 400);
                gradient.addColorStop(0, value.mid);
                gradient.addColorStop(0.8, value.low);
                gradientColors[key] = gradient;
            }

            return gradientColors;
        },
    },
    watch: {
        data: {
            handler() {
                this.refreshChart();
            },
        },
        options: {
            handler() {
                this.refreshChart();
            },
        },
        type: {
            handler() {
                this.refreshChart();
            },
        },
    },
    created() {
        // Disable automatic style injection
        // More info: https://www.chartjs.org/docs/latest/getting-started/integration.html#content-security-policy
        Chart.platform.disableCSSInjection = true;
    },
    mounted() {
        this.ctx = this.$refs.canvas.getContext("2d");
        this.refreshChart();
    },
    beforeDestroy() {
        this.chart.destroy();
    },
    methods: {
        refreshChart() {
            // define background gradient as soon as valid data is available
            if (typeof this.data === "object") {
                this.data.datasets = (this.data.datasets || []).map((dataset, index) => {
                    if (this.colors.length) {
                        dataset.backgroundColor = this.gradientColors[this.colors?.[index]]
                            ? this.gradientColors[this.colors?.[index]]
                            : this.gradientColors.default;
                        dataset.pointBackgroundColor = this.colorSet[this.colors?.[index]]
                            ? this.colorSet[this.colors?.[index]].high
                            : this.colorSet.default.high;
                        dataset.borderColor = this.colorSet[this.colors?.[index]]
                            ? this.colorSet[this.colors?.[index]].high
                            : this.colorSet.default.high;
                    }

                    dataset.borderWidth = 3;

                    if (this.options?.elements?.point?.radius !== 0) {
                        dataset.radius = 3;
                    }

                    return dataset;
                });
            }

            if (this.chart) {
                this.chart.destroy();
            }

            if (this.ctx) {
                Chart.defaults.global.defaultFontFamily =
                    "'Circular', 'Helvetica Neue', Helvetica, Arial, sans-serif";

                this.chart = new Chart(this.ctx, {
                    type: this.type,
                    data: this.data,
                    options: this.options,
                });
            }
        },
    },
};
</script>

<style scoped>
/* Disable automatic style injection */
/* More info: https://www.chartjs.org/docs/latest/getting-started/integration.html#content-security-policy */
@import "../../node_modules/chart.js/dist/Chart.min.css";

.l-chart {
    display: flex;
    justify-content: center;
}
</style>
